import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./listingCard.html";
import { PopoverHorizontalOrientation, PopoverVerticalOrientation } from 'wds/popover';
import { isExternalAgentCardPresentationAvailable } from '~/modules/helpers/externalAgentSearchHelper';
import { getCategoryFilterOrder } from '~/modules/helpers/searchSortOptionsHelper';
import { buildSlackMarketplaceLink, hasUsableSlackMarketplaceSlug } from '~/modules/helpers/slackMarketplaceLinkHelper';
import { siteConfigStore } from '~/modules/stores/siteConfigStore';
import { generateSimpleBadge } from '~/modules/helpers/badgeHelper';
import { i18nStore, selectCurrentLanguage, WireI18n } from '~/modules/stores/i18nStore';
import { getListingLogoMediaId } from '~/modules/utilities/assetUtilities';
import { generateMediaUrl } from '~/modules/utilities/asset_utils';
import { handleException } from '~/modules/utilities/error_utilities';
import { JAPANESE_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { PLUGIN_LISTING_ID_PATTERN, SKILL_LISTING_ID_PATTERN } from '~/modules/utilities/listingIdPatterns';
import { isSlackListingsEnabled } from '~/modules/utilities/slackPilot';
const BADGE_BREAKPOINTS_EN = [{
  maxWidth: 320,
  maxBadges: 1
}, {
  maxWidth: 400,
  maxBadges: 2
}, {
  maxWidth: 500,
  maxBadges: 3
}, {
  maxWidth: Infinity,
  maxBadges: 4
}];
const BADGE_BREAKPOINTS_JA = [{
  maxWidth: 320,
  maxBadges: 1
}, {
  maxWidth: 500,
  maxBadges: 2
}, {
  maxWidth: 600,
  maxBadges: 3
}, {
  maxWidth: Infinity,
  maxBadges: 4
}];
function getBadgeBreakpointsForLanguage() {
  const currentLanguage = selectCurrentLanguage(i18nStore.getState());
  return currentLanguage === JAPANESE_LANGUAGE_CODE ? BADGE_BREAKPOINTS_JA : BADGE_BREAKPOINTS_EN;
}
const DEFAULT_CARD_WIDTH_PX = 400;

// Skill listings are identified by type 'ai-skill' or a slug-shaped `skill-…` oafId (no Salesforce
// record backs a skill). Pattern imported from the leaf module `listingIdPatterns.ts` (zero
// imports), which keeps this module out of listing_utils.ts's heavier listing_service import chain.
const SKILL_BADGE_ICON = 'work_order_type';
// A plugin listing (a bundle of skills) is identified the same way, by type 'ai-plugin' or a
// slug-shaped `plugin-…` oafId.
const PLUGIN_BADGE_ICON = 'apex_plugin';
export const RESIZE_DEBOUNCE_MS = 50;
export const POPOVER_WIDTH = 240;
const POPOVER_ARROW_WIDTH = 18;
const POPOVER_ARROW_HALF_WIDTH = POPOVER_ARROW_WIDTH / 2;
const POPOVER_MARGIN = 8;
const POPOVER_BASE_CSS_OFFSET = -50;
const POPOVER_BASE_CSS_OFFSET_MOBILE_TABLET = -100;
class ListingCard extends LightningElement {
  constructor(...args) {
    super(...args);
    this.showBadgePopover = false;
    this.showCategoryPopover = false;
    this.positionIndex = void 0;
    this.variant = 'standard';
    this.platformType = undefined;
    this.headerBadgeLabel = undefined;
    this.headerBadgeIcon = undefined;
    this.hideListingTypeBadge = false;
    /** Renders the card as display-only content while preserving its existing visual treatment. */
    this.disableNavigation = false;
    this.selectedCategoryKeys = [];
    // Opt-in (default false): fill the tags row with as many category tags as fit on one line,
    // collapsing the remainder into the "+N" overflow. Only the v2 Specs-tab required-listings card
    // sets this; every other usage keeps the fixed visibleCategoryLimit behavior. See visibleTags below.
    this.fitTagsToLine = false;
    this.visibleCategoryLimit = 1;
    this.i18n = void 0;
    // Number of category tags that fit on one line when fitTagsToLine is on. null = not yet measured
    // (renders all tags until the first measurement clamps them, which avoids a hidden-forever row in
    // environments without layout such as jsdom).
    this._fitVisibleTagCount = null;
    // Set when even a single tag plus the "+N" control cannot fit on one line (very narrow reflow).
    // The row then drops the single-line/nowrap modifier and wraps instead, so the overflow control
    // is never clipped out of reach. See measureVisibleTagCount / tagsContainerClass.
    this._fitRowWraps = false;
    this._cardWidthState = {
      width: DEFAULT_CARD_WIDTH_PX
    };
    this.resizeObserver = null;
    this.observedElement = null;
    this.isObserverActive = false;
    this.resizeDebounceTimeout = null;
    this.popoverOrientation = {
      vertical: PopoverVerticalOrientation.BOTTOM,
      horizontal: PopoverHorizontalOrientation.RIGHT
    };
  }
  get cardWidth() {
    return this._cardWidthState.width;
  }
  set listingCardData(listing) {
    const {
      i18n
    } = i18nStore.getState();
    this._listingCardData = {
      ...listing,
      positionIndex: this.positionIndex,
      image: this.getImageFromListing(listing),
      listingCategories: listing.listingCategories.filter(Boolean).map(category => {
        const filter = typeof category.filter === 'string' ? category.filter.trim() || undefined : undefined;
        return {
          ...category,
          filter,
          ariaLabel: i18n.shared.tagAriaLabel({
            CATEGORY_NAME: category.name
          }),
          id: `${listing.oafId}-${category.name?.replace(/ /g, '').trim()}`,
          isNavigable: Boolean(filter)
        };
      }),
      additionalProps: listing.additionalProps,
      description: listing.additionalProps?.agentDescription || listing.description
    };
  }
  getImageFromListing(listingCardData) {
    if (listingCardData.resolvedImageUrl) {
      try {
        const {
          protocol
        } = new URL(listingCardData.resolvedImageUrl);
        if (protocol === 'http:' || protocol === 'https:') {
          return listingCardData.resolvedImageUrl;
        }
      } catch {
        // Malformed values fall through to the normal media-ID resolution below.
      }
    }
    if (listingCardData.logos) {
      const mediaId = getListingLogoMediaId(listingCardData.logos);
      if (mediaId) {
        return generateMediaUrl(mediaId);
      }
    }
    if (listingCardData.image) {
      return generateMediaUrl(listingCardData.image);
    }
    return undefined;
  }
  get altTextMessage() {
    const {
      i18n
    } = i18nStore.getState();
    return i18n.listingTile.altTextMessage({
      TITLE: this.listingCardData.title
    });
  }
  get listingCardData() {
    return this._listingCardData;
  }
  get screenReaderMessageForRating() {
    const {
      i18n
    } = i18nStore.getState();
    return i18n.listingTile.screenReaderRating({
      RATING: this.listingCardData.averageRating
    });
  }
  get chatLabel() {
    const {
      i18n
    } = i18nStore.getState();
    return i18n.listingCard.chatBadgeText();
  }
  get agentforceContentTitle() {
    const {
      i18n
    } = i18nStore.getState();
    return i18n.listingCard.agentforceContentTitle();
  }
  get popoverContentId() {
    return `popover-content-${this.listingCardData?.oafId || 'default'}`;
  }
  get overflowButtonId() {
    return `overflow-button-${this.listingCardData?.oafId || 'default'}`;
  }
  get popoverContentTitleId() {
    return `${this.popoverContentId}-title`;
  }
  get showChat() {
    return this.listingCardData.chatEnabled;
  }
  get isPreview() {
    return this.variant === 'preview';
  }
  get isWithBadge() {
    return this.variant === 'with-badge';
  }
  get isStandardCard() {
    return this.variant === 'standard';
  }

  // A skill listing carries type 'ai-skill' from the recommendations service; its oafId is also
  // slug-shaped (`skill-…`) rather than a Salesforce id, which we accept as a resilient fallback.
  // Skills surface both on the Skills Marketplace (every card) and intermixed with apps in search
  // results (only some cards), so the card self-detects rather than relying on the caller.
  get isSkillListing() {
    const {
      type,
      oafId
    } = this.listingCardData ?? {};
    return type === 'ai-skill' || SKILL_LISTING_ID_PATTERN.test(oafId ?? '');
  }

  // A plugin listing carries type 'ai-plugin' or a slug-shaped `plugin-…` oafId, mirroring skill
  // detection above.
  get isPluginListing() {
    const {
      type,
      oafId
    } = this.listingCardData ?? {};
    return type === 'ai-plugin' || PLUGIN_LISTING_ID_PATTERN.test(oafId ?? '');
  }

  // The badge shown in the card header. An explicit headerBadgeLabel from the caller wins (kept
  // for backward compatibility); otherwise the containing page may suppress the automatic
  // skill/plugin type badge, which remains enabled by default everywhere else.
  get resolvedHeaderBadgeLabel() {
    if (this.headerBadgeLabel) {
      return this.headerBadgeLabel;
    }
    if (this.hideListingTypeBadge) {
      return undefined;
    }
    if (!isExternalAgentCardPresentationAvailable(siteConfigStore.getState().domain)) {
      return undefined;
    }
    const {
      i18n
    } = i18nStore.getState();
    if (this.isPluginListing) {
      return i18n.skills.pluginBadge();
    }
    if (this.isSkillListing) {
      return i18n.skills.skillBadge();
    }
    return undefined;
  }
  get resolvedHeaderBadgeIcon() {
    if (this.headerBadgeLabel) {
      return this.headerBadgeIcon;
    }
    if (this.hideListingTypeBadge) {
      return undefined;
    }
    if (!isExternalAgentCardPresentationAvailable(siteConfigStore.getState().domain)) {
      return undefined;
    }
    if (this.isPluginListing) {
      return PLUGIN_BADGE_ICON;
    }
    return this.isSkillListing ? SKILL_BADGE_ICON : undefined;
  }

  // Theme for the header badge: blue for both plugin and skill, distinguished only by icon
  // glyph (badge only renders for skill/plugin today, so a single value covers both).
  get resolvedHeaderBadgeTheme() {
    return 'blue';
  }

  // Reserve top-right room for the header badge only when the standard card actually shows one,
  // so the title clears the badge on skill cards without padding every other card.
  get standardCardCssClass() {
    return this.resolvedHeaderBadgeLabel ? 'has-header-badge' : '';
  }

  // Single platform-badge contract: the value may arrive either as the public `platformType`
  // attribute (set per record by cardGrid, or page-level by results.ts) or embedded in
  // listingCardData by a producer (listingToCardData / recommendationToCardData). The explicit
  // attribute wins; otherwise fall back to the card data so a direct wds-listing-card consumer
  // gets the right badge without the caller having to rebind the attribute. (W-24063286)
  get resolvedPlatformType() {
    return this.platformType ?? this.listingCardData?.platformType;
  }
  get showSalesforceIcon() {
    if (!this.isWithBadge) {
      return false;
    }

    // Default badge for 'salesforce', undefined, or any unknown value; also the fallback for
    // any platform without its own icon, so the badge slot is never left blank (W-24063286).
    return !this.showSlackIcon && !this.showConsultantsIcon;
  }
  get showSlackIcon() {
    if (!this.isWithBadge) {
      return false;
    }

    // Badge reflects platform identity, so a slack listing shows the Slack icon regardless of
    // the pilot flag or whether a marketplace slug resolved. Linking out is gated separately
    // (see isSlackMarketplaceListing). (W-24063286)
    return this.resolvedPlatformType === 'slack';
  }
  get showConsultantsIcon() {
    if (!this.isWithBadge) {
      return false;
    }
    return this.resolvedPlatformType === 'consultants';
  }

  // Platform-icon accessible descriptions, sourced from i18n so the badge is localized as well
  // as visual (e.g. コンサルタント on JP experiences) rather than a hard-coded English string.
  // Reuses the existing search platform-filter labels. (W-24063286)
  get salesforceIconDescription() {
    return i18nStore.getState().i18n.search.platformFilter.salesforce();
  }
  get slackIconDescription() {
    return i18nStore.getState().i18n.search.platformFilter.slack();
  }
  get consultantsIconDescription() {
    return i18nStore.getState().i18n.search.platformFilter.consultants();
  }
  get isSlackMarketplaceListing() {
    const {
      additionalProps
    } = this.listingCardData;
    return isSlackListingsEnabled() && this.resolvedPlatformType === 'slack' && hasUsableSlackMarketplaceSlug(additionalProps?.slackSlug);
  }
  get cardUrl() {
    const {
      additionalProps,
      oafId
    } = this.listingCardData;
    if (this.isSlackMarketplaceListing && additionalProps?.slackSlug) {
      const {
        domain
      } = siteConfigStore.getState();
      const currentLanguage = selectCurrentLanguage(i18nStore.getState());
      return buildSlackMarketplaceLink({
        domain,
        language: currentLanguage,
        oafId,
        slug: additionalProps.slackSlug
      }).url;
    }
    return this.listingCardData.listingUrl;
  }
  get cardTarget() {
    return this.isSlackMarketplaceListing ? '_blank' : '_self';
  }
  get cardRel() {
    return this.isSlackMarketplaceListing ? 'noopener noreferrer' : undefined;
  }
  get cardAriaLabel() {
    if (!this.isSlackMarketplaceListing) {
      return undefined;
    }
    const {
      i18n
    } = i18nStore.getState();
    const publisher = this.listingCardData.publisher ? i18n.listingTile.labelByMessage({
      PUBLISHER: this.listingCardData.publisher
    }) : undefined;
    const rating = this.showRatings ? i18n.listingTile.screenReaderRating({
      RATING: this.listingCardData.averageRating
    }) : undefined;
    return [this.listingCardData.title, publisher, rating, i18n.listingCard.opensInNewTab()].filter(part => Boolean(part)).join(' - ');
  }

  // Skills and plugins have no reviews feature yet, so never show ratings for them
  // even if reviewsAmount is populated.
  get showRatings() {
    return !this.isSkillListing && !this.isPluginListing && (this.listingCardData?.reviewsAmount ?? 0) > 0;
  }
  get tags() {
    const categories = this.listingCardData.listingCategories;
    const selectedCategories = this.selectedCategoryKeys;
    if (!selectedCategories?.length) {
      return categories;
    }

    // Sort so that selected categories appear first. Both top-level and
    // subcategory keys are ordered by the full flattened filter panel order
    // (canonical category order with subcategories sorted alphabetically within
    // each category). Unselected categories follow in their original order.
    const selectedSet = new Set(selectedCategories);
    const {
      i18nStaticCache
    } = i18nStore.getState();
    const filterOrder = getCategoryFilterOrder(i18nStaticCache.oaf);
    const orderIndex = new Map(filterOrder.map((k, i) => [k, i]));
    const selected = categories.filter(cat => cat.filterKey && selectedSet.has(cat.filterKey));
    selected.sort((a, b) => {
      const ai = orderIndex.has(a.filterKey) ? orderIndex.get(a.filterKey) : Infinity;
      const bi = orderIndex.has(b.filterKey) ? orderIndex.get(b.filterKey) : Infinity;
      return ai - bi;
    });
    const unselected = categories.filter(cat => !cat.filterKey || !selectedSet.has(cat.filterKey));
    return [...selected, ...unselected];
  }

  // How many tags to show before the "+N" overflow. In fit-to-line mode this is the measured count
  // (or all tags until the first measurement runs); otherwise the configured visibleCategoryLimit.
  get maxVisibleTags() {
    if (this.fitTagsToLine) {
      return this._fitVisibleTagCount ?? this.tags.length;
    }
    return this.visibleCategoryLimit;
  }
  get visibleTags() {
    return this.tags.slice(0, this.maxVisibleTags);
  }
  get showCategoryOverflow() {
    return this.tags.length > this.maxVisibleTags;
  }

  // The measurement row (fit-to-line mode only) renders every tag off-screen plus a worst-case
  // "+N" button so renderedCallback can measure natural widths and decide how many fit on one line.
  get showTagsMeasureRow() {
    return this.fitTagsToLine && this.tags.length > 0;
  }

  // Worst-case overflow label ("+<all tags>") used to reserve overflow-button width while measuring.
  get categoryOverflowLabelMax() {
    return `+${this.tags.length}`;
  }

  // Adds a modifier that switches the tags row to single-line (no wrap) when fitting to the line.
  // The single-line modifier is dropped when even one tag + the "+N" control cannot fit (see
  // _fitRowWraps), so the row wraps rather than clipping the overflow control out of reach.
  get tagsContainerClass() {
    if (!this.fitTagsToLine || this._fitRowWraps) {
      return 'tags-container';
    }
    return 'tags-container tags-container--single-line';
  }

  // Gates the tags section: hide it entirely when it would hold no icon and no tag,
  // so an empty section doesn't leave a blank row + margin under the description.
  get showTags() {
    return this.agentforceBadges.length > 0 || this.visibleTags.length > 0;
  }
  get overflowTagCount() {
    return Math.max(0, this.tags.length - this.maxVisibleTags);
  }
  get categoryOverflowLabel() {
    return `+${this.overflowTagCount}`;
  }
  get categoryOverflowAriaLabel() {
    const {
      i18n
    } = i18nStore.getState();
    const count = this.overflowTagCount;
    return i18n.listingCard.categoryOverflowAriaLabel({
      COUNT: count
    });
  }
  get categoryOverflowAccessibleLabel() {
    return `${this.categoryOverflowLabel}, ${this.categoryOverflowAriaLabel}`;
  }
  get categoryPopoverContentId() {
    return `category-popover-content-${this.listingCardData?.oafId || 'default'}`;
  }
  get categoryOverflowButtonId() {
    return `category-overflow-button-${this.listingCardData?.oafId || 'default'}`;
  }
  get categoryPopoverAriaLabel() {
    if (!this.showCategoryPopover) {
      return '';
    }
    return this.tags.map(tag => tag.name).join(', ');
  }
  get allCategoriesLabel() {
    const {
      i18n
    } = i18nStore.getState();
    return i18n.listingCard.allCategoriesLabel();
  }
  get cardFrameType() {
    return this.listingCardData.sponsored ? 'default' : 'elevation-2';
  }
  get agentIconAltText() {
    const {
      i18n: i18nModule
    } = i18nStore.getState();
    return i18nModule.oaf.agenticDetails.agentIconAltText();
  }
  get agentforceBadges() {
    const {
      i18n: i18nModule
    } = i18nStore.getState();
    const {
      additionalProps
    } = this.listingCardData;
    const badges = [];
    if (!additionalProps) {
      return badges;
    }
    const agentCount = additionalProps.agentCount ?? 0;
    const topicCount = additionalProps.topicCount ?? 0;
    const actionCount = additionalProps.actionCount ?? 0;
    const mcpServerCount = additionalProps.mcpServerCount ?? 0;

    // Actions Badge (first)
    if (actionCount > 0) {
      const actionsLabel = i18nModule.oaf.agenticDetails.actions({
        numActions: actionCount.toString()
      });
      badges.push(generateSimpleBadge(actionsLabel));
    }

    // Topics Badge (Subagents) (second)
    if (topicCount > 0) {
      const topicsLabel = i18nModule.oaf.agenticDetails.topics({
        numTopics: topicCount.toString()
      });
      badges.push(generateSimpleBadge(topicsLabel));
    }

    // Agents Badge (third)
    if (agentCount > 0) {
      badges.push(generateSimpleBadge(i18nModule.oaf.agenticDetails.agents({
        numAgents: agentCount.toString()
      })));
    }

    // MCP Servers Badge (fourth)
    if (mcpServerCount > 0) {
      badges.push(generateSimpleBadge(i18nModule.oaf.agenticDetails.mcpServers({
        numMCPServers: mcpServerCount.toString()
      })));
    }
    return badges;
  }
  calculateMaxBadgesForWidth(width) {
    const breakpoints = getBadgeBreakpointsForLanguage();
    const breakpoint = breakpoints.find(bp => width <= bp.maxWidth);
    return breakpoint?.maxBadges ?? breakpoints[breakpoints.length - 1].maxBadges;
  }
  get maxVisibleBadges() {
    return this.calculateMaxBadgesForWidth(this.cardWidth);
  }
  get visibleBadges() {
    return this.agentforceBadges.slice(0, this.maxVisibleBadges);
  }
  get overflowBadgeCount() {
    return Math.max(0, this.agentforceBadges.length - this.maxVisibleBadges);
  }
  get showOverflowCount() {
    return this.overflowBadgeCount > 0;
  }
  get overflowLabel() {
    return `+${this.overflowBadgeCount}`;
  }
  get overflowAriaLabel() {
    const {
      i18n
    } = i18nStore.getState();
    const count = this.overflowBadgeCount;
    return i18n.listingCard.badgeOverflowAriaLabel({
      COUNT: count
    });
  }
  get overflowAccessibleLabel() {
    return `${this.overflowLabel}, ${this.overflowAriaLabel}`;
  }

  // Measure the off-screen tags row and decide how many category tags fit on a single line, then
  // update the visible count. Greedy left-to-right fit; when not everything fits, room for the "+N"
  // button is reserved so the last visible tag never collides with it. No-ops unless fit-to-line is
  // enabled, and bails out cleanly when layout is unavailable (e.g. jsdom returns 0-width rects),
  // leaving _fitVisibleTagCount as-is so all tags render rather than a permanently clipped row.
  measureVisibleTagCount() {
    if (!this.fitTagsToLine) {
      return;
    }
    const totalTags = this.tags.length;
    if (totalTags === 0) {
      if (this._fitVisibleTagCount !== 0) {
        this._fitVisibleTagCount = 0;
      }
      if (this._fitRowWraps) {
        this._fitRowWraps = false;
      }
      return;
    }
    const container = this.refs?.tagsContainer;
    const measureRow = this.refs?.tagsMeasure;
    if (!container || !measureRow) {
      return;
    }
    const tagEls = Array.from(measureRow.querySelectorAll('[data-measure-tag]'));
    if (tagEls.length !== totalTags) {
      return;
    }
    const tagWidths = tagEls.map(el => el.getBoundingClientRect().width);
    const availableWidth = container.clientWidth;
    // No layout yet (initial paint / jsdom): leave the count untouched so all tags render.
    if (availableWidth <= 0 || tagWidths.some(width => width <= 0)) {
      return;
    }
    const computed = getComputedStyle(container);
    const gap = parseFloat(computed.columnGap || computed.gap || '0') || 0;

    // The agentforce icon shares the row and is always shown first; subtract it from the budget.
    // It carries its own margin-right on top of the flex gap, so reserve both after the icon.
    const icon = container.querySelector('.agentforce-icon');
    const iconMarginRight = icon ? parseFloat(getComputedStyle(icon).marginRight) || 0 : 0;
    const iconWidth = icon ? icon.getBoundingClientRect().width + iconMarginRight + gap : 0;
    const overflowButton = measureRow.querySelector('[data-measure-overflow]');
    const overflowWidth = overflowButton ? overflowButton.getBoundingClientRect().width : 0;
    const budget = availableWidth - iconWidth;

    // reserved = width kept for the "+N" button (0 when no overflow). A gap sits between every
    // tag and between the last tag and the button, so each tag added past the reserve costs
    // gap + width; the reserve itself already holds the button width.
    const countThatFits = reserved => {
      let used = reserved;
      let count = 0;
      for (const width of tagWidths) {
        const next = used + (count > 0 || reserved > 0 ? gap : 0) + width;
        if (next > budget) {
          break;
        }
        used = next;
        count += 1;
      }
      return count;
    };

    // First see if every tag fits with no overflow button at all.
    let visibleCount = countThatFits(0);
    let rowWraps = false;
    if (visibleCount < totalTags) {
      // Overflow is unavoidable — recompute leaving room for the "+N" button, and always show
      // at least one tag so the row is never just the overflow control.
      const withOverflow = countThatFits(overflowWidth);
      // Not even one tag + the "+N" control fits on the line (very narrow reflow): forcing one
      // tag would push the overflow control past a clipped single-line row, so wrap instead.
      rowWraps = withOverflow === 0;
      visibleCount = Math.max(1, withOverflow);
    }
    if (visibleCount !== this._fitVisibleTagCount) {
      this._fitVisibleTagCount = visibleCount;
    }
    if (rowWraps !== this._fitRowWraps) {
      this._fitRowWraps = rowWraps;
    }
  }
  connectedCallback() {
    try {
      this.resizeObserver = new ResizeObserver(([entry]) => {
        if (this.resizeDebounceTimeout) {
          clearTimeout(this.resizeDebounceTimeout);
        }
        this.resizeDebounceTimeout = setTimeout(() => {
          try {
            const newWidth = entry?.contentRect.width;
            if (newWidth && newWidth > 0) {
              const currentMaxBadges = this.calculateMaxBadgesForWidth(this._cardWidthState.width);
              const newMaxBadges = this.calculateMaxBadgesForWidth(newWidth);
              if (currentMaxBadges !== newMaxBadges) {
                this._cardWidthState = {
                  width: newWidth
                };
              }
              // Re-fit the tags row to the new width (fit-to-line mode only).
              this.measureVisibleTagCount();
              this.showBadgePopover = false;
              this.showCategoryPopover = false;
            }
          } catch (error) {
            handleException(error, 'listingCard.resizeObserverCallback', {
              debugData: {
                currentWidth: this._cardWidthState.width,
                newWidth: entry?.contentRect.width
              }
            });
          }
        }, RESIZE_DEBOUNCE_MS);
      });
    } catch (error) {
      handleException(error, 'listingCard.connectedCallback.resizeObserverInit', {
        debugData: {
          isSupported: typeof ResizeObserver !== 'undefined'
        }
      });
      this.resizeObserver = null;
    }
  }
  renderedCallback() {
    // Runs on every render and converges: it only writes _fitVisibleTagCount when the fitted count
    // changes, so the follow-up re-render measures the same value and stops (no render loop).
    this.measureVisibleTagCount();
    if (!this.resizeObserver) {
      return;
    }
    const cardElement = this.refs?.cardElement;
    if (!cardElement) {
      return;
    }
    if (this.isObserverActive && cardElement === this.observedElement) {
      return;
    }
    if (this.observedElement && this.observedElement !== cardElement) {
      this.resizeObserver.unobserve(this.observedElement);
      this.isObserverActive = false;
    }
    this.resizeObserver.observe(cardElement);
    this.observedElement = cardElement;
    this.isObserverActive = true;
  }
  disconnectedCallback() {
    this.showBadgePopover = false;
    this.showCategoryPopover = false;
    if (this.resizeDebounceTimeout) {
      clearTimeout(this.resizeDebounceTimeout);
      this.resizeDebounceTimeout = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.observedElement = null;
    this.isObserverActive = false;
  }
  handleBadgeClick(event) {
    try {
      event.preventDefault();
      event.stopPropagation();
      this.showCategoryPopover = false;
      this.calculatePopoverPosition(event.target);
      this.showBadgePopover = true;
    } catch (e) {
      handleException(e, 'listingCard.handleBadgeClick');
    }
  }
  handleTooltipKeyboardEvent(event) {
    this.handlePopoverKeyEvent(event, 'badge');
  }
  handlePopoverClose() {
    this.showBadgePopover = false;
  }
  handleCategoryClick(event) {
    try {
      event.preventDefault();
      event.stopPropagation();
      this.showBadgePopover = false;
      this.calculateCategoryPopoverPosition(event.target);
      this.showCategoryPopover = true;
    } catch (e) {
      handleException(e, 'listingCard.handleCategoryClick');
    }
  }
  handleCategoryKeyboardEvent(event) {
    this.handlePopoverKeyEvent(event, 'category');
  }
  handleCategoryPopoverClose() {
    this.showCategoryPopover = false;
  }
  handlePopoverKeyEvent(event, targetPopover) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        if (targetPopover === 'badge') {
          this.showBadgePopover = false;
        } else {
          this.showCategoryPopover = false;
        }
        break;
      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        if (targetPopover === 'badge') {
          this.showCategoryPopover = false;
          this.showBadgePopover = !this.showBadgePopover;
        } else {
          this.showBadgePopover = false;
          this.showCategoryPopover = !this.showCategoryPopover;
        }
        break;
      case 'Tab':
        if (targetPopover === 'badge') {
          this.showBadgePopover = false;
        } else {
          this.showCategoryPopover = false;
        }
        break;
      default:
        break;
    }
  }
  calculateCategoryPopoverPosition(overflowButton) {
    this.calculatePopoverPositionForRef('categoryOverflowButton', overflowButton);
  }
  get popoverAriaLabel() {
    return this.agentforceBadges.map(badge => badge.value).join(', ');
  }
  calculatePopoverPosition(overflowButton) {
    this.calculatePopoverPositionForRef('overflowButton', overflowButton);
  }
  calculatePopoverPositionForRef(refName, overflowButton) {
    try {
      const popoverEl = this.refs?.[refName];
      if (!popoverEl || !overflowButton) {
        return;
      }
      const btn = overflowButton.getBoundingClientRect();
      const card = this.refs?.cardElement?.getBoundingClientRect();
      if (!card) {
        return;
      }
      const buttonCenterX = btn.left + btn.width / 2;
      const buttonCenterRelToCard = buttonCenterX - card.left;
      const arrowX = buttonCenterRelToCard - POPOVER_ARROW_HALF_WIDTH;
      popoverEl.style.setProperty('--popover-arrow-horizontal-right', `${arrowX}px`);
      const desiredLeft = buttonCenterRelToCard - POPOVER_WIDTH / 2;
      const minLeft = POPOVER_MARGIN;
      const maxLeft = Math.max(minLeft, card.width - POPOVER_WIDTH - POPOVER_MARGIN);
      const clampedLeft = Math.min(Math.max(desiredLeft, minLeft), maxLeft);
      const gap = POPOVER_BASE_CSS_OFFSET - clampedLeft;
      const gapMobile = POPOVER_BASE_CSS_OFFSET_MOBILE_TABLET - clampedLeft;
      popoverEl.style.setProperty('--popover-arrow-gap', `${gap}px`);
      popoverEl.style.setProperty('--popover-arrow-gap-mobile', `${gapMobile}px`);
    } catch (e) {
      handleException(e, 'listingCard.calculatePopoverPositionForRef');
    }
  }
  handleAnalytics(event) {
    if (this.disableNavigation && event.detail?.eventType === 'click') {
      event.stopPropagation();
      return;
    }
    event.detail.listing = this.listingCardData;
  }
  /*LWC compiler v7.1.3*/
}
ListingCard.delegatesFocus = true;
_registerDecorators(ListingCard, {
  publicProps: {
    positionIndex: {
      config: 0
    },
    variant: {
      config: 0
    },
    platformType: {
      config: 0
    },
    headerBadgeLabel: {
      config: 0
    },
    headerBadgeIcon: {
      config: 0
    },
    hideListingTypeBadge: {
      config: 0
    },
    disableNavigation: {
      config: 0
    },
    selectedCategoryKeys: {
      config: 0
    },
    fitTagsToLine: {
      config: 0
    },
    visibleCategoryLimit: {
      config: 0
    },
    cardWidth: {
      config: 1
    },
    listingCardData: {
      config: 3
    }
  },
  track: {
    showBadgePopover: 1,
    showCategoryPopover: 1,
    _fitVisibleTagCount: 1,
    _fitRowWraps: 1,
    _cardWidthState: 1
  },
  wire: {
    i18n: {
      adapter: WireI18n,
      config: function ($cmp) {
        return {};
      }
    }
  },
  fields: ["resizeObserver", "observedElement", "isObserverActive", "resizeDebounceTimeout", "popoverOrientation"]
});
const __lwc_component_class_internal = _registerComponent(ListingCard, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;